// Portada de market: un punto por cada ubicación de negocio que toma turnos por Ágora en el AMBA, en su
// lugar real, todos iguales (nadie se destaca). Los puntos vienen de Cyclone (/marketplace/dots, sin
// nombres) y se proyectan en el servidor sobre una imagen estática de Mapbox (light-v11) con la misma
// proyección Web Mercator, así el SVG solo dibuja círculos.

const API_URL = process.env.NEXT_PUBLIC_CYCLONE_API_URL || 'https://cyclone.agora.red/v1';
const MAPBOX_TOKEN =
  'pk.eyJ1IjoiYmV0b3lhbmVzIiwiYSI6ImNrenBocnVjczE0eWkybm1namxuaTVxMDUifQ.G5hOThUzepjWwPPERXDqZg';

// Encuadre de la imagen base (1280×800, el máximo de la API estática; @2x para pantallas densas).
// CABA queda a la derecha del titular.
export const FIELD = { width: 1280, height: 800, lng: -58.565, lat: -34.6, zoom: 10.6 };
// Teléfono: vertical, con CABA en la mitad de abajo (arriba van el titular y el buscador, sobre el velo).
const FIELD_PHONE = { width: 400, height: 860, lng: -58.46, lat: -34.53, zoom: 10.7 };
// Panel «En el mapa»: a nivel de calle, centrado en el barrio con más negocios (ver `densestCenter`).
const WAYS_ZOOM = 14.3;
const WAYS_SIZE = { width: 640, height: 600 };
const WAYS_DEFAULT_CENTER = { lat: -34.6, lng: -58.44 };
const BOUNDS = { swLat: -34.86, swLng: -58.8, neLat: -34.42, neLng: -58.22 };

const TILE = 512;
const worldSize = (zoom) => TILE * 2 ** zoom;

/** Longitud/latitud → píxel de la imagen de FIELD (Web Mercator, igual que Mapbox). */
export function projectToField(lat, lng, field = FIELD) {
  const size = worldSize(field.zoom);
  const toX = (lon) => ((lon + 180) / 360) * size;
  const toY = (la) => {
    const sin = Math.sin((la * Math.PI) / 180);
    return (0.5 - Math.log((1 + sin) / (1 - sin)) / (4 * Math.PI)) * size;
  };
  return {
    x: toX(lng) - toX(field.lng) + field.width / 2,
    y: toY(lat) - toY(field.lat) + field.height / 2
  };
}

/** Imagen base, muy tenue en la portada. Mapbox pide atribución: la página la muestra. */
export const fieldImageUrl = (field = FIELD) =>
  `https://api.mapbox.com/styles/v1/mapbox/light-v11/static/${field.lng},${field.lat},${field.zoom},0/${field.width}x${field.height}@2x?attribution=false&logo=false&access_token=${MAPBOX_TOKEN}`;

/** Centro de la celda (~1,5 km) con más negocios: el panel del mapa muestra una zona con vida. */
export function densestCenter(dots, cell = 0.014) {
  const counts = new Map();
  let best = null;
  for (const [lat, lng] of dots) {
    const key = `${Math.floor(lat / cell)}:${Math.floor(lng / cell)}`;
    const n = (counts.get(key) || 0) + 1;
    counts.set(key, n);
    if (!best || n > best.n) best = { key, n };
  }
  if (!best) return WAYS_DEFAULT_CENTER;
  const [i, j] = best.key.split(':').map(Number);
  return { lat: Math.round((i + 0.5) * cell * 1e4) / 1e4, lng: Math.round((j + 0.5) * cell * 1e4) / 1e4 };
}

const inside = (field) => ({ x, y }) => x >= -10 && x <= field.width + 10 && y >= -10 && y <= field.height + 10;
const round = ({ x, y }) => [Math.round(x * 10) / 10, Math.round(y * 10) / 10];

const emptyField = () => ({
  desktop: [],
  phone: [],
  ways: { ...WAYS_SIZE, zoom: WAYS_ZOOM, ...WAYS_DEFAULT_CENTER, dots: [] }
});

/** Puntos proyectados `[x, y]` para escritorio y teléfono. Si Cyclone no responde, sin puntos. */
export async function fetchHeroField() {
  try {
    const query = new URLSearchParams(Object.entries(BOUNDS).map(([k, v]) => [k, String(v)]));
    // En Workers la home se renderiza por pedido: `cf` cachea el subpedido en el edge.
    const res = await fetch(`${API_URL}/marketplace/dots?${query}`, {
      next: { revalidate: 21600 },
      cf: { cacheTtlByStatus: { '200-299': 21600, '400-599': 0 } }
    });
    if (!res.ok) return emptyField();
    const { dots = [] } = await res.json();
    const project = (field) =>
      dots.map(([lat, lng]) => projectToField(lat, lng, field)).filter(inside(field)).map(round);
    const ways = { ...WAYS_SIZE, zoom: WAYS_ZOOM, ...densestCenter(dots) };
    return { desktop: project(FIELD), phone: project(FIELD_PHONE), ways: { ...ways, dots: project(ways) } };
  } catch {
    return emptyField();
  }
}

/**
 * Una etiqueta que se puede mostrar en la portada: los nombres de servicio son texto libre y algunos
 * vienen numerados o con el precio adentro («01- ESTILISMO/CORRE…», «Técnica Clásica $35.000»).
 */
export const isTidyLabel = (label) =>
  Boolean(label) && label.length <= 40 && !/\$|^\s*\d+\s*[-.)/]/.test(label) && label !== label.toUpperCase();

/**
 * Fotos de trabajos (sin nombres) para la home: la fila de la portada y el panel «Por trabajo». Una por
 * negocio, hasta 14; el orden de los negocios rota por día en Cyclone, así que nadie queda fijo primero.
 */
export async function fetchHomeWorks() {
  try {
    const query = 'swLat=-34.7&swLng=-58.53&neLat=-34.53&neLng=-58.33&limit=40';
    const res = await fetch(`${API_URL}/marketplace/works?${query}`, {
      next: { revalidate: 21600 },
      cf: { cacheTtlByStatus: { '200-299': 21600, '400-599': 0 } }
    });
    if (!res.ok) return [];
    const { works = [] } = await res.json();
    // Una por negocio y solo las que tienen precio: la etiqueta dice servicio y precio, nunca quién.
    // La etiqueta es el nombre del servicio: el epígrafe es texto libre del negocio («01- ESTILISMO/COR…»).
    const seen = new Set();
    return works
      .map((w) => ({ ...w, caption: (w.service_name || w.caption || '').trim() }))
      .filter((w) => w.price && isTidyLabel(w.caption) && !seen.has(w.vendor_id) && seen.add(w.vendor_id))
      .slice(0, 14)
      .map(({ url, caption, price, duration }) => ({ url, caption, price, duration }));
  } catch {
    return [];
  }
}
